import React, { useCallback, useLayoutEffect, useRef } from 'react';
import { t } from 'ttag';
import styled from '@xstyled/styled-components';
import { Button } from '~/ui/buttons/Button';
import { VoiceInput } from '~/training/sequence/templates/items/chatBot/components/ui/VoiceInput';
import SendIcon from '~/assets/icons/sendMessage.svg';

const MessageComposerForm = styled.form`
  flex: 0 0 auto;
  padding: 20px 120px;

  display: flex;
  align-items: flex-end;

  --composer-control-height: 38px;

  label {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  }

  textarea {
    flex: 1 1 auto;
    font-family: Raleway;
    font-size: 12px;
    line-height: 1.5;
    font-weight: 400;
    text-align: left;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;
    background-color: #f5f5f5;
    padding: 10px 15px;
    border-radius: 5px;
    min-height: var(--composer-control-height);
    height: auto;
    max-height: 180px;
    overflow-y: auto;
    resize: none;

    &::placeholder {
      font-family: Raleway;
      font-size: 12px;
      font-weight: 400;
      line-height: 1.5;
      text-align: left;
      text-underline-position: from-font;
      text-decoration-skip-ink: none;
      color: #6f6f6f;
    }
  }

  button {
    margin-left: 11px;
    flex: 0 0 auto;
    height: var(--composer-control-height);
    display: inline-flex;
    align-items: center;
  }

  @media (max-width: 768px) {
    padding: 12px 20px 16px;
  }
`;

const SubmitButton = styled(Button)`
  padding: 6px 20px;

  @media (max-width: 768px) {
    padding: 6px;
    min-width: unset;
  }
`;

const SendLabel = styled.span`
  @media (max-width: 768px) {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  }
`;

const SendIconWrapper = styled.span`
  display: none;

  @media (max-width: 768px) {
    display: inline-flex;
    align-items: center;
  }
`;

interface MessageComposerProps {
  message: string;
  onMessageChange: (message: string) => void;
  onSendMessage: (message: string) => void;
  onVoiceTranscript: (transcript: string) => void;
  loading: boolean;
  disabled: boolean;
  placeholder: string;
}

export const MessageComposer: React.FC<MessageComposerProps> = ({
  message,
  onMessageChange,
  onSendMessage,
  onVoiceTranscript,
  loading,
  disabled,
  placeholder
}) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const handledEnterRef = useRef(false);
  const allowLineBreakRef = useRef(false);

  const autosize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = Math.min(el.scrollHeight, 180) + 'px';
  }, []);

  useLayoutEffect(() => {
    autosize();
  }, [message, autosize]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      const native = event.nativeEvent as KeyboardEvent;
      if ((native as KeyboardEvent & { isComposing?: boolean }).isComposing)
        return;

      if (event.key === 'Enter') {
        if (
          event.shiftKey ||
          native.altKey ||
          native.ctrlKey ||
          native.metaKey
        ) {
          allowLineBreakRef.current = true;
          return;
        }

        if (!disabled) {
          handledEnterRef.current = true;
          event.preventDefault();
          onSendMessage(message);
        }
      }
    },
    [onSendMessage, message, disabled]
  );

  const handleBeforeInput = useCallback(
    (e: React.FormEvent<HTMLTextAreaElement>) => {
      const native = e.nativeEvent as InputEvent;
      if (native.inputType !== 'insertLineBreak') return;

      if (handledEnterRef.current) {
        handledEnterRef.current = false;
        e.preventDefault();
        return;
      }

      if (allowLineBreakRef.current) {
        allowLineBreakRef.current = false;
        return;
      }

      if (!disabled) {
        e.preventDefault();
        onSendMessage(message);
      }
    },
    [onSendMessage, message, disabled]
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      onSendMessage(message);
    },
    [onSendMessage, message]
  );

  return (
    <MessageComposerForm
      aria-label="Message Composer"
      onSubmit={handleSubmit}
      autoComplete="off"
    >
      <label htmlFor="chat-bot-message">{t`Type your message`}</label>
      <textarea
        id="chat-bot-message"
        rows={1}
        enterKeyHint="send"
        inputMode="text"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        placeholder={placeholder}
        value={message}
        disabled={loading || disabled}
        ref={textareaRef}
        onBeforeInput={handleBeforeInput}
        onChange={e => {
          if (!disabled) {
            onMessageChange(e.target.value);
          }
          const el = textareaRef.current;
          if (el) {
            el.style.height = '0px';
            el.style.height = Math.min(el.scrollHeight, 180) + 'px';
          }
        }}
        onKeyDown={handleKeyDown}
      />
      <VoiceInput
        onTranscript={onVoiceTranscript}
        disabled={loading || disabled}
        title={disabled ? t`Chat completed` : t`Voice input`}
      />
      <SubmitButton
        disabled={loading || disabled}
        title={disabled ? t`Chat completed` : t`Send message`}
        type="submit"
      >
        <SendLabel>{t`Send`}</SendLabel>
        <SendIconWrapper aria-hidden="true">
          <SendIcon width={20} height={20} />
        </SendIconWrapper>
      </SubmitButton>
    </MessageComposerForm>
  );
};
